import "../styles/css/request-estimate.css"
import React, {useCallback, useEffect, useRef, useState} from "react";
import {useNavigate} from "react-router-dom";
import {google_api_key} from "../services/common.services";
import {usePlacesWidget} from "react-google-autocomplete";
import {getAverageCenterOfPolygon, SplitAddressFromGoogleAddressComponents} from "../shared/sharedFunctions";
import {
    DeepRequired,
    FieldErrorsImpl,
    FieldPath,
    FieldValues,
    GlobalError,
    RegisterOptions,
    useForm, UseFormRegisterReturn
} from "react-hook-form";
import {
    APIProvider,
    ControlPosition,
    Map,
    MapCameraChangedEvent,
    Marker,
    useMap,
    useMapsLibrary
} from "@vis.gl/react-google-maps";
import FormControl from "../shared/components/FormControl";
import CustomInput from "../shared/components/CustomInput";
import {userService} from "../services/userService";
import {publicCustomerService} from "../services/publicCustomerService";
import notificationService from "../services/NotificationService";
import AddressInput, {BARRECA_CENTER} from "../shared/components/AutocompleteAddressInput";
import MapWithDrawing from "../shared/components/MapWithDrawing";





const EstimateRequestPage: React.FC = () => {
    const navigate = useNavigate();
    const [location, setLocation] = useState({ lat: 42.1539, lng: -88.052183 });
    const { register, trigger, getValues, formState: { errors }} = useForm();
    const [polygon, setPolygon] = useState<google.maps.Polygon>(null);
    const [polygonPath, setPolygonPath] = useState<any>(null);
    const [activeProperty, setActiveProperty] = useState<string>('Residential')
    const [place, setPlace] = useState(null);
    const [mapDetails, setMapDetails] = useState(null);
    const [totalArea, setTotalArea] = useState(0);
    const [mapsLoaded, setMapsLoaded] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [addressMissing, setAddressMissing] = useState(false);
    
    async function handleSubmit() {
        const validation = await trigger();
        if(!place) setAddressMissing(true)
        else setAddressMissing(false)
        if(!validation || !place) return;
        setIsSubmitting(true);
        const addressSplit = SplitAddressFromGoogleAddressComponents(place.address_components)
        const all = getValues();
        const roundToNearest = 5;
        const postObject = {
            firstName: all.firstName === '' ? null : all.firstName,
            lastName: all.lastName,
            businessName: all.businessName,
            email: all.email,
            phone: all.phone === '' ? null : all.phone,
            isCommercialAddress: activeProperty === 'Commercial',
            streetNumber: addressSplit.streetNumber,
            streetPrefix: addressSplit.streetPrefix,
            streetName: addressSplit.streetName,
            streetSuffix: addressSplit.streetSuffix,
            streetPostDirection: addressSplit.streetPostDirection,
            city: addressSplit.city,
            county: addressSplit.county,
            state: addressSplit.state,
            postalCode: addressSplit.postalCode,
            standardizedAddress: place.formatted_address,
            squareFeet: totalArea === 0 ? null : Math.ceil(totalArea / roundToNearest) * roundToNearest,
            mapImageDataBase64: totalArea === 0 ? null : await capture(),
            mapAreaVertices: polygonPath?.map(node => ({
                Lat:  node.lat,
                Long: node.lng
                }))
        }
        setIsSubmitting(false)
        Promise.resolve(
            publicCustomerService.makeEstimateRequest(postObject)
        ).then(postRes => {
            if(!postRes) return;
            if(postRes.quoteRequested)
                navigate('thanks');
            else
                notificationService.error(postRes.errorMessage)
        });
    }

    const calculateArea = (poly: google.maps.Polygon) => {
        const areaMeters = google.maps.geometry.spherical.computeArea(poly.getPath());
        const areaFeet = areaMeters * 10.7639;
        setTotalArea(areaFeet);
    };

    const clearPolygons = () => {
        setPolygon(null);
        setPolygonPath(null);
    };

    const handlePolygon = (poly: google.maps.Polygon) => {
        const path = poly.getPath().getArray().map(p => p.toJSON());
        calculateArea(poly);
        setPolygon(poly);
        setPolygonPath(path);
        
        google.maps.event.addListener(poly.getPath(), 'set_at', () => calculateArea(poly));
        google.maps.event.addListener(poly.getPath(), 'insert_at', () => calculateArea(poly));
        google.maps.event.addListener(poly, 'rightclick', (e: any) => {
            if (e.vertex != null) {
                poly.getPath().removeAt(e.vertex);
                calculateArea(poly);
            }
        });
    };

    const buildUrl = (width: number, height: number) => {
        const path = polygonPath
            .map(pt => `${pt.lat},${pt.lng}`)
            .join('|');

        const centerPoint = polygonPath.length > 0
            ? getAverageCenterOfPolygon(polygonPath)
            : mapDetails.center;
        const zoom   = mapDetails.zoom;

        const params = new URLSearchParams({
            center: `${centerPoint.lat},${centerPoint.lng}`,
            zoom:   zoom.toString(),
            size:   `${width}x${height}`,
            maptype: 'hybrid',
            path: [
                `fillcolor:0x00FFFF80`,
                `color:0x0000FF`,
                `weight:2`,
                path,
            ].join('|'),
            key: google_api_key,
        });

        return `https://maps.googleapis.com/maps/api/staticmap?${params}`;
    };

    const blobToBase64 = (blob: Blob): Promise<string> =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
                const dataUrl = reader.result as string;
                resolve(dataUrl.split(',')[1] || '');
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });

    const MIN_SIDE = 128;
    const STEP = 32;
    const TARGET = 256_000;

    const capture = async (): Promise<string> => {
        if (polygonPath.length < 3) {
            console.log('no polygon was drawn');
            return '';
        }

        let side = 1440;
        let blob: Blob;

        do {
            const url = buildUrl(side, side);
            const res = await fetch(url);
            if (!res.ok) throw new Error(res.statusText);
            blob = await res.blob();

            if (blob.size > TARGET) {
                side = Math.max(MIN_SIDE, side - STEP);
            }
        } while (blob.size > TARGET && side > MIN_SIDE);

        return await blobToBase64(blob);
    };

    const lastRun = useRef<number>(0);
    const handleCameraChange = useCallback((ev: MapCameraChangedEvent) => {
        const now = Date.now();
        // only run at most every x ms
        if (now - lastRun.current >= 100) {
            lastRun.current = now;
            setMapDetails(ev.detail);
        }
    }, [setMapDetails]);
    return (
        <div className='request-body'>
            <div className="container estimate-container">
                <div className="page-card estimate-form">
                    <div className="row gx-5 justify-content-between">
                        <div className="col-lg-4 mb-3 mb-lg-0">
                            <h2 className="mb-4">Request an estimate</h2>
                            <fieldset className="form-group mb-3">
                                <CustomInput label={'Your First Name'} required name={'firstName'} className={"form-control form-control-lg"} register={register} errors={errors}/>

                            </fieldset>
                            <fieldset className="form-group mb-3">
                                <CustomInput label={'Your Last Name'} required name={'lastName'} className={"form-control form-control-lg"} register={register} errors={errors}/>

                            </fieldset>
                            <fieldset className="form-group mb-3">
                                <CustomInput type={'email'} label={'Your Email'} required name={'email'} className={"form-control form-control-lg"} register={register} errors={errors}/>
                                
                            </fieldset>
                            <fieldset className="form-group mb-3">
                                <CustomInput type={'tel'} label={'Your Phone'} required name={'phone'} className={"form-control form-control-lg"} register={register} errors={errors}/>
                            </fieldset>
                            <fieldset className="form-group mb-3">
                                <label className="form-label">
                                    Your Address
                                    <sup className="text-danger">*</sup>
                                </label>
                                <div className="mb-3">
                                    {mapsLoaded ? (
                                        <AddressInput onLocation={coords => setLocation(coords)} setPlace={setPlace}
                                                      addressMissing={addressMissing} className={"form-control form-control-lg"}/>
                                    ) : (
                                        <input placeholder="Your Address" className="form-control form-control-lg"/>
                                    )}
                                </div>
                            </fieldset>
                            <fieldset className="form-group mb-3">
                                <label>Address Type</label>
                                <div className="btn-group" role="group" aria-label="Basic example">
                                    <button type="button" onClick={() => setActiveProperty('Residential')}
                                            className={activeProperty === 'Residential' ? "btn active" : 'btn'}>Residential</button>
                                    <button type="button" onClick={() => setActiveProperty('Commercial')} className={activeProperty === 'Commercial' ? "btn active" : 'btn'}>Commercial</button>
                                </div>
                            </fieldset>
                            <fieldset className="form-group mb-3">
                                <CustomInput label="Your Business Name"
                                             name="businessName"
                                             register={register}
                                             className={"form-control form-control-lg"}
                                             errors={errors}
                                             required={activeProperty === 'Commercial'}
                                             disabled={activeProperty !== 'Commercial'}
                                             placeholder="Your Business Name"/>
                            </fieldset>
                            <fieldset className="form-group mb-3">
                                <label>Map Reference <span className="optional-text">(optional)</span></label>
                                <p className="small">should probably be a discount so people actually do it, Lorem ipsum use map to draw bounds. Lorem ipsum it’s optional, not
                                    final, Barreca might adjust,  etc</p>
                                <button type="button" onClick={clearPolygons} className="reset-map-btn">reset map</button>
                            </fieldset>
                            <hr className="my-4"/>
                            <button disabled={isSubmitting} onClick={() => handleSubmit()} type="submit" className="send-estimate-btn mb-3"><i
                                className="fas fa-paper-plane"></i> Send Request
                            </button>
                            <p className="text-center small mb-0" style={{color: '#797979'}}>Lorem ipsum legalese. By
                                requesting quote you xyz. Contact Barreca if you need to. Something else.</p>
                        </div>
                        <div className="col-lg">
                            <div className="estimate-map">
                                <MapWithDrawing handleCameraChange={handleCameraChange} location={location} handlePolygon={handlePolygon} onLoad={() => setMapsLoaded(true)}/>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
export default EstimateRequestPage;
